import {memo, forwardRef, useEffect, useRef, useState, useMemo} from 'react';
import {Button, Popover, TextField, TextFieldProps} from '@shopify/polaris';
import {useI18n} from '@shopify/react-i18n';
import {LegacyRichTextEditor} from 'components/LegacyRichTextEditor';
import {RichTextEditor} from 'components/RichTextEditor';
import {ContentSwitcher} from 'components/ContentSwitcher';
import {useToggle} from '@shopify/react-hooks';
import {TranslatableResourceType} from 'types';
import {MenuVerticalIcon} from '@shopify/polaris-icons';
import {FilePicker} from 'components/FilePicker';
import classNames from 'classnames';
import {LocalizableContent} from 'types/graphql/default-types';
import {parseGidType} from '@web-utilities/other';
import {snakeCase} from 'lodash';

import {TranslatableField, TranslatableResourceLayoutFormProps} from '../types';

import {isSoftCapProps, isHardCapProps} from './utils';
import {FIELDS_MAPPING} from './constants';
import styles from './TableCellField.scss';
import {TranslatableCellField} from './types';
import {FieldStatusDetails, FieldStatusQuickView} from './components';

export type Props = Omit<
  TranslatableField,
  | 'groupKey'
  | 'isOnlyMarketLocalizable'
  | 'status'
  | 'isContextualizedThemeContent'
> & {
  readOnly?: boolean;
  disabled?: boolean;
  setField: TranslatableResourceLayoutFormProps['setField'];
  status?: TranslatableField['status'];
};

const TEXT_FIELD_MAX_HEIGHT = 500;

// eslint-disable-next-line react/display-name
const TableCellField = forwardRef<HTMLDivElement, Props>(function (
  {
    fieldKey,
    gidKey,
    reference,
    label,
    value,
    translatableResourceType,
    setField,
    type,
    display: {readOnlySuffix, suffix, placeholder},
    metafieldDetails,
    readOnly = false,
    disabled,
    error,
    status,
    dirty,
    fileReference,
  },
  ref,
) {
  const [i18n] = useI18n();
  const {
    value: fieldIsFocused,
    setFalse: blurField,
    setTrue: focusField,
  } = useToggle(false);
  const {
    value: popoverVisible,
    setFalse: hidePopover,
    setTrue: showPopover,
  } = useToggle(false);
  const {
    value: isGrabbingFocus,
    setFalse: releaseFocusForPopover,
    setTrue: grabFocusForPopOver,
  } = useToggle(false);
  const [richTextEditorInitiated, setRichTextEditorInitiated] = useState(false);

  const richTextEditorValue = useRef(value);

  // workaround to fix an issue where the RTE is initializing with the incorrect value on the first render
  useEffect(() => {
    if (value !== richTextEditorValue.current) {
      richTextEditorValue.current = value;
      setRichTextEditorInitiated(false);
    }
  }, [richTextEditorValue, value]);

  // as above
  useEffect(() => {
    if (!richTextEditorInitiated) {
      setRichTextEditorInitiated(true);
    }
  }, [richTextEditorInitiated]);

  useEffect(() => {
    if (fieldIsFocused) {
      releaseFocusForPopover();
      showPopover();
    } else {
      grabFocusForPopOver();
      hidePopover();
    }
  }, [
    fieldIsFocused,
    grabFocusForPopOver,
    hidePopover,
    releaseFocusForPopover,
    showPopover,
  ]);

  let displayLength: number | undefined;
  let maxLength: number | undefined;
  let fieldAdditionalProps: TranslatableCellField['props'];
  let fieldComponent: TranslatableCellField['component'] = 'text_field';

  const onChange = (newValue: string) => {
    setField(gidKey, newValue);
  };
  // Copied from: https://stackoverflow.com/a/15458987
  function isJsonString(str: string) {
    try {
      JSON.parse(str);
    } catch (err) {
      return false;
    }
    return true;
  }

  const isOnlineStoreTheme = [
    TranslatableResourceType.OnlineStoreThemeJsonTemplate,
    TranslatableResourceType.OnlineStoreThemeSectionGroup,
    TranslatableResourceType.OnlineStoreThemeSettingsCategory,
    TranslatableResourceType.OnlineStoreThemeLocaleContent,
    TranslatableResourceType.OnlineStoreThemeSettingsDataSections,
  ].includes(translatableResourceType);
  const isNotification =
    translatableResourceType === TranslatableResourceType.EmailTemplate;
  const isMetaField = [
    TranslatableResourceType.Metafield,
    TranslatableResourceType.Metaobject,
  ].includes(translatableResourceType);

  const fieldType = type || metafieldDetails?.type.toUpperCase();

  if (FIELDS_MAPPING?.[translatableResourceType]?.[fieldKey]) {
    const field = FIELDS_MAPPING![translatableResourceType]![fieldKey];

    if (field?.props) {
      fieldAdditionalProps = field.props;
    }

    if (field?.component) {
      fieldComponent = field.component;
    }
  }

  if (fileReference) {
    fieldComponent = 'file_picker';
  }

  switch (fieldType) {
    case LocalizableContent.RichTextField:
    case LocalizableContent.InlineRichText:
      fieldComponent = 'rich_text_editor';
      break;
    case LocalizableContent.Html:
      if (isOnlineStoreTheme || isJsonString(reference)) {
        break;
      }

      fieldComponent = 'legacy_rich_text_editor';
      break;
    case LocalizableContent.MultiLineTextField:
      fieldAdditionalProps = {
        ...fieldAdditionalProps,
        multiline: true,
      };
      break;
  }

  const isLegacyRichTextEditorField =
    fieldComponent === 'legacy_rich_text_editor';
  const isRichTextEditorField = fieldComponent === 'rich_text_editor';

  if (isSoftCapProps(fieldAdditionalProps)) {
    displayLength = fieldAdditionalProps.suggestedLength;
  } else if (isHardCapProps(fieldAdditionalProps)) {
    displayLength = fieldAdditionalProps.maxLength;
    maxLength = fieldAdditionalProps.maxLength;
  }

  const b64Key = useMemo(() => {
    return btoa(fieldKey).replace(/[=+/]/g, '');
  }, [fieldKey]);

  const fieldSuffix = value ? suffix : '';
  const textFieldBaseProps = {
    id: `${readOnly ? 'readonly' : 'editable'}_${b64Key}`,
    multiline: fieldAdditionalProps?.multiline || reference.length > 50,
    maxHeight: TEXT_FIELD_MAX_HEIGHT,
    autoComplete: 'off',
    type: 'text' as TextFieldProps['type'],
    labelHidden: true,
    label,
    onChange,
    error,
    value,
    disabled,
    readOnly,
    suffix: readOnly ? readOnlySuffix : fieldSuffix,
    placeholder,
    onFocus: disabled ? () => {} : focusField,
    onBlur: disabled ? () => {} : blurField,
  };

  const translateTextFieldWithLimit = (
    currentLength: number,
    displayLength: number | undefined,
  ) => {
    let translated;
    try {
      translated = i18n.translate('TextFieldWithLimit.charactersUsed', {
        count: displayLength ?? 0,
        currentLength,
        maxLength: displayLength ?? 100,
      });
    } catch {
      translated = '';
    }
    return translated;
  };

  const showStatus =
    !(
      translatableResourceType ===
      TranslatableResourceType.OnlineStoreThemeLocaleContent
    ) &&
    fieldComponent !== 'file_picker' &&
    !readOnly &&
    Boolean(value);

  const popoverActivator = showStatus ? (
    <div className={styles.AccessibilityActivator}>
      <Button
        variant="plain"
        accessibilityLabel={i18n.translate(
          'TextFieldWithLimit.moreDetailsLabel',
        )}
        icon={MenuVerticalIcon}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            grabFocusForPopOver();
            showPopover();
            event.stopPropagation();
            event.preventDefault();
          }
        }}
        onClick={() => {
          grabFocusForPopOver();
          showPopover();
        }}
      />
    </div>
  ) : (
    <></>
  );
  const statusMarkup = showStatus ? (
    <>
      <Popover
        active={popoverVisible}
        activator={popoverActivator}
        onClose={() => {
          if (isGrabbingFocus) {
            hidePopover();
            releaseFocusForPopover();
          }
        }}
        autofocusTarget={isGrabbingFocus ? 'first-node' : 'none'}
        preferredAlignment="right"
      >
        <FieldStatusDetails gidKey={gidKey} status={status} dirty={dirty} />
      </Popover>
      {!fieldIsFocused && !dirty && <FieldStatusQuickView status={status} />}
    </>
  ) : null;

  const cellField = (
    <div
      className={classNames(
        styles.TableCellWrapper,
        fieldComponent === 'text_field' && styles.TableCellWrapper__TextField,
        readOnly && styles.TableCellWrapper__ReadOnly,
        isLegacyRichTextEditorField &&
          styles.TableCellWrapper__LegacyRichTextEditor,
        isRichTextEditorField && styles.TableCellWrapper__RichTextEditor,
      )}
      ref={ref}
    >
      <ContentSwitcher>
        <ContentSwitcher.When condition={isLegacyRichTextEditorField}>
          <div
            className={classNames(fieldIsFocused && styles.RichTextEditorFocus)}
          >
            <LegacyRichTextEditor
              {...textFieldBaseProps}
              readOnly={readOnly || disabled}
              label=""
              showHtmlInitially={isNotification}
            />
          </div>
        </ContentSwitcher.When>
        <ContentSwitcher.When
          condition={
            isRichTextEditorField && isMetaField && richTextEditorInitiated
          }
        >
          <RichTextEditor
            {...textFieldBaseProps}
            readOnly={readOnly || disabled}
            value={richTextEditorValue.current}
            mode="json"
            preferredPreset="metafield"
            onChange={(value) => {
              richTextEditorValue.current = value;
              onChange(value);
            }}
          />
        </ContentSwitcher.When>
        <ContentSwitcher.When
          condition={
            isRichTextEditorField && !isMetaField && richTextEditorInitiated
          }
        >
          <RichTextEditor
            {...textFieldBaseProps}
            readOnly={readOnly || disabled}
            value={richTextEditorValue.current}
            mode="html"
            preferredPreset={
              type === LocalizableContent.InlineRichText
                ? 'inlineHtml'
                : undefined
            }
            onChange={(value) => {
              richTextEditorValue.current = value;
              onChange(value);
            }}
          />
        </ContentSwitcher.When>
        <ContentSwitcher.When
          condition={fieldComponent === 'text_field_with_limit' && !readOnly}
        >
          <div className={styles.EditableTextFieldWrapper}>
            <TextField
              {...textFieldBaseProps}
              maxLength={maxLength}
              maxHeight={TEXT_FIELD_MAX_HEIGHT - 30}
              helpText={translateTextFieldWithLimit(
                value.length,
                displayLength,
              )}
            />
          </div>
        </ContentSwitcher.When>
        <ContentSwitcher.When condition={fieldComponent === 'file_picker'}>
          <FilePicker
            originalFile={
              readOnly ? fileReference?.reference : fileReference?.value
            }
            readOnly={readOnly}
            supportedFileTypes={fileReference?.fileTypesAllowed}
            resourceId={value}
            onChange={(selectedFile) => {
              if (selectedFile) {
                const resourceType = snakeCase(
                  parseGidType(selectedFile.id),
                ).toUpperCase() as TranslatableResourceType;
                if (
                  selectedFile?.displayName &&
                  fileReference?.fieldType === 'filename'
                ) {
                  if (resourceType === TranslatableResourceType.Video) {
                    onChange(
                      `shopify://files/videos/${selectedFile?.displayName}`,
                    );
                  } else if (
                    resourceType === TranslatableResourceType.MediaImage
                  ) {
                    onChange(
                      `shopify://shop_images/${selectedFile?.displayName}`,
                    );
                  }
                } else {
                  onChange(selectedFile.id || '');
                }
              } else {
                onChange('');
              }
            }}
          />
        </ContentSwitcher.When>
        <ContentSwitcher.Fallback>
          <TextField {...textFieldBaseProps} />
        </ContentSwitcher.Fallback>
      </ContentSwitcher>
    </div>
  );

  return (
    <>
      {cellField}
      {statusMarkup}
    </>
  );
});

export default memo(TableCellField);
